import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Chart, ChartArea, ChartLegend, ChartSeries, ChartSeriesItem, ChartSeriesLabels, SeriesHoverEvent } from "@progress/kendo-react-charts";
import { useLocalization } from "@progress/kendo-react-intl";
import { Loader } from "@progress/kendo-react-indicators";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { AccessToken } from "../../config/AccessToken";
import { KpiHoverTooltip } from "./KpiHoverTooltip";
import { averageOf, formatRelativeTime, getBatteryGradientColor, hoursSince, parseServerDateTime } from "./dashboardUtils";

type BeaconsKpiData = {
  total: number;
  regularCount: number;
  loaded: number;
  unloaded: number;
  unknownBatteryCount: number;
  averageBattery: number | null;
  lastUpdate: Date | null;
};

const EMPTY_DATA: BeaconsKpiData = { total: 0, regularCount: 0, loaded: 0, unloaded: 0, unknownBatteryCount: 0, averageBattery: null, lastUpdate: null };
const REGULAR_HOURS = 24;

const COLOR_LOADED = '#16a34a';
const COLOR_UNLOADED = '#ef4444';
const COLOR_UNKNOWN = '#111827';
const COLOR_EMPTY = '#e5e7eb';

const kpiLabelStyle = { fontSize: 'var(--fs-small)', fontWeight: 700, color: '#6b7280', letterSpacing: '0.03em', textTransform: 'uppercase' as const };

export const BeaconsKpiCard = () => {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [instance, accessTokenRequest] = AccessToken();
  const localizationService = useLocalization();
  const languageId = localStorage.getItem('languageId');
  const batteryThreshold = Number(localStorage.getItem('batteryPercentage') ?? 20);

  const [data, setData] = useState<BeaconsKpiData>(EMPTY_DATA);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);

  const t = (key: string, fallback: string) => localizationService.toLanguageString(key, fallback);

  const labels = {
    regular: t('dashboard.widget.devices.regular', 'Regolari'),
    total: t('dashboard.widget.devices.total', 'Totali'),
    threshold: t('dashboard.widget.devices.threshold', 'Soglia batteria'),
    charged: t('dashboard.widget.devices.charged', 'Carichi'),
    uncharged: t('dashboard.widget.devices.uncharged', 'Scarichi'),
    unknown: t('dashboard.widget.devices.unknown', 'Sconosciuti'),
    lastUpdate: t('dashboard.widget.devices.last_update', 'Ultimo agg.'),
    averageBattery: t('dashboard.widget.beacons.average_battery', 'Batteria media'),
    batteryStatus: t('dashboard.widget.beacons.battery_status', 'Stato batteria')
  };

  useEffect(() => {
    const fetchBeacons = async () => {
      setLoading(true);

      const commonOperations = async (accessToken: any) => {
        const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

        try {
          const res = await axios.get(`${SERVER_URL}/beacon/list`, { headers, params: { enabled: true } });
          const beacons: any[] = res?.data?.data ?? [];

          let regularCount = 0;
          let loaded = 0;
          let unloaded = 0;
          let unknownBatteryCount = 0;
          let lastUpdate: Date | null = null;
          const batteryValues: number[] = [];

          beacons.forEach((beacon) => {
            const beaconDate = parseServerDateTime(beacon.lastSeen);
            const hours = hoursSince(beaconDate);
            if (hours != null && hours <= REGULAR_HOURS) {
              regularCount++;
            }
            if (beaconDate && (!lastUpdate || beaconDate > lastUpdate)) {
              lastUpdate = beaconDate;
            }
            if (beacon.battery != null) {
              batteryValues.push(beacon.battery);
              if (beacon.battery <= batteryThreshold) {
                unloaded++;
              } else {
                loaded++;
              }
            } else {
              unknownBatteryCount++;
            }
          });

          setData({
            total: beacons.length,
            regularCount,
            loaded,
            unloaded,
            unknownBatteryCount,
            averageBattery: averageOf(batteryValues),
            lastUpdate
          });
        } catch {
          setData(EMPTY_DATA);
        } finally {
          setLoading(false);
        }
      };

      if (localStorage.getItem('accessToken') != null) {
        await commonOperations(localStorage.getItem('accessToken'));
      } else {
        await instance.initialize();
        await instance.acquireTokenSilent(accessTokenRequest)
          .then((accessTokenResponse: any) => commonOperations(accessTokenResponse.accessToken))
          .catch((error: any) => {
            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }
            setLoading(false);
          });
      }
    };

    void fetchBeacons();
  }, []);

  const hasBatteryData = data.loaded + data.unloaded + data.unknownBatteryCount > 0;

  const chartData = useMemo(() => {
    if (!hasBatteryData) {
      // Placeholder ring keeps the donut footprint stable when there is nothing to plot.
      return [{ category: '', value: 1, color: COLOR_EMPTY, placeholder: true }];
    }
    const slices: { category: string; value: number; color: string; placeholder?: boolean }[] = [
      { category: labels.charged, value: data.loaded, color: COLOR_LOADED },
      { category: labels.uncharged, value: data.unloaded, color: COLOR_UNLOADED }
    ];
    if (data.unknownBatteryCount > 0) {
      slices.push({ category: labels.unknown, value: data.unknownBatteryCount, color: COLOR_UNKNOWN });
    }
    return slices;
  }, [hasBatteryData, data.loaded, data.unloaded, data.unknownBatteryCount, labels.charged, labels.uncharged, labels.unknown]);

  const handleSeriesHover = (event: SeriesHoverEvent) => {
    if (!hasBatteryData) {
      return;
    }
    const nativeEvent = event?.nativeEvent;
    setHover({ x: nativeEvent?.clientX ?? 0, y: nativeEvent?.clientY ?? 0 });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', color: '#1f2937' }}>
      <div
        style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', alignItems: 'center', gap: '12px' }}
        onMouseLeave={() => setHover(null)}
      >
        <div>
          <span style={{ fontSize: '1.4rem', fontWeight: 800, color: COLOR_LOADED }}>{loading ? '…' : data.regularCount}</span>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}> / {data.total}</span>
          <div style={kpiLabelStyle}>{`${labels.regular} (${REGULAR_HOURS}H) / ${labels.total}`}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {loading ? (
            <Loader size="small" type="infinite-spinner" themeColor="primary" />
          ) : (
            <div style={{ position: 'relative', width: '230px', height: '230px' }}>
              <Chart style={{ width: '230px', height: '230px' }} onSeriesHover={handleSeriesHover} transitions={false}>
                <ChartArea background="transparent" />
                <ChartLegend visible={false} />
                <ChartSeries>
                  <ChartSeriesItem
                    type="donut"
                    data={chartData}
                    categoryField="category"
                    field="value"
                    colorField="color"
                    tooltip={{ visible: false }}
                  >
                    <ChartSeriesLabels
                      visible={true}
                      position="center"
                      background="none"
                      color="#fff"
                      font="700 0.75rem sans-serif"
                      content={(e: any) => (e.dataItem?.placeholder || !e.value ? '' : `${e.value} (${Math.round((e.percentage ?? 0) * 100)}%)`)}
                    />
                  </ChartSeriesItem>
                </ChartSeries>
              </Chart>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none'
                }}
              >
                <span style={{ ...kpiLabelStyle, fontSize: '0.6875rem', lineHeight: 1.15, textAlign: 'center', maxWidth: '60px', marginBottom: '2px' }}>{labels.threshold}</span>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#1f2937' }}>{batteryThreshold}%</span>
              </div>
            </div>
          )}
          {hasBatteryData ? (
            <div style={{ display: 'flex', gap: '10px', marginTop: '6px', fontSize: '0.8125rem', color: '#4b5563' }}>
              <span><span style={{ color: COLOR_LOADED }}>●</span> {labels.charged}</span>
              <span><span style={{ color: COLOR_UNLOADED }}>●</span> {labels.uncharged}</span>
              {data.unknownBatteryCount > 0 && <span><span style={{ color: COLOR_UNKNOWN }}>●</span> {labels.unknown}</span>}
            </div>
          ) : (
            <div style={{ marginTop: '6px', maxWidth: '230px', textAlign: 'center', fontSize: '0.8125rem', color: '#6b7280' }}>
              {localizationService.toLanguageString('dashboard.widget.devices_status.no_value', 'Nessun dato')}
            </div>
          )}
        </div>

        {hover && (
          <KpiHoverTooltip
            x={hover.x}
            y={hover.y}
            title={labels.batteryStatus}
            rows={[
              { label: labels.charged, value: data.loaded },
              { label: labels.uncharged, value: data.unloaded },
              ...(data.unknownBatteryCount > 0 ? [{ label: labels.unknown, value: data.unknownBatteryCount }] : [])
            ]}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: '0 0 auto' }}>
          <div>
            <span
              style={{
                fontSize: '1.4rem',
                fontWeight: 800,
                color: data.averageBattery != null ? getBatteryGradientColor(data.averageBattery) : '#1f2937'
              }}
            >
              {data.averageBattery != null ? `${data.averageBattery}%` : '—'}
            </span>
            <div style={kpiLabelStyle}>{labels.averageBattery}</div>
          </div>
          <div>
            <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1f2937' }}>{data.lastUpdate ? formatRelativeTime(data.lastUpdate, languageId) : '—'}</span>
            <div style={kpiLabelStyle}>{labels.lastUpdate}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
